import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import api from '../services/api';
import { User, LoginAttemptResponse, LoginResponse, TenantInfo, TwoFactorChallengeResponse } from '../types';
import authService from '../services/authService';
import { checkForUpdates } from '../serviceWorkerRegistration';
import {
  PWA_INSTALL_DISMISSED_KEY,
  PWA_INSTALL_ELIGIBLE_KEY,
} from '../constants/pwaInstallPrompt';
import { clearTenantAdminGate } from '../utils/tenantAdminGate';
import { clearTesoreriaFiltrosStorage } from '../utils/tesoreriaFiltrosStorage';
import { clearGrupoCajaStorage } from '../utils/grupoCajaStorage';
import { clearFechaOperativaSession } from '../utils/fechaOperativaStore';
import { clearPermisosCache } from '../utils/permisoKeys';
import { clearGlobalSucursalCompaniaPrefs } from '../utils/companiaPreferida';
import { devError } from '../utils/devLog';
import {
  anotherTabOwnsSlot,
  AuthBroadcastMessage,
  broadcastAuthMessage,
  browserSessionHeaders,
  clearAllAuthStorage,
  clearTabExplicitLogout,
  clearTabSession,
  consumeTabHandoffQuery,
  getAuthBroadcastChannel,
  getLastUsedTenantId,
  getTabSessionId,
  getTenantsList,
  hasBrowserSession,
  isTabExplicitLogout,
  markTabExplicitLogout,
  persistTabSession,
  readTabSession,
  seedSessionFromLastUsedIfNeeded,
  setTenantsList,
  writeLastUsedFromSession,
} from '../utils/sessionAuthStore';

interface AuthContextType {
  /** Falso mientras esta solapa intenta reanudar la cookie del navegador. */
  authReady: boolean;
  isAuthenticated: boolean;
  user: User | null;
  token: string | null;
  /** Tenant actual (multi-tenant). Null si login legacy. */
  tenantId: number | null;
  tenantName: string | null;
  tenantCode: string | null;
  /** Lista de tenants del usuario (para selector "Cambiar empresa"). */
  tenants: TenantInfo[];
  /** Estado inferido del último login de esta sesión; null si la sesión no pasó por login. */
  twoFactorEnabled: boolean | null;
  updateTwoFactorStatus: (enabled: boolean) => void;
  login: (username: string, password: string) => Promise<TwoFactorChallengeResponse | null>;
  completeTwoFactorLogin: (challengeToken: string, code: string) => Promise<void>;
  /** Cierra sesión en esta pestaña. Si allTabs=true, cierra también las demás. */
  logout: (options?: { allTabs?: boolean }) => void;
  refreshToken: () => Promise<void>;
  /** Cambia el tenant activo y devuelve un nuevo JWT. */
  switchTenant: (tenantId: number, options?: { reload?: boolean }) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const applySessionToState = (
  snap: ReturnType<typeof readTabSession>,
  setters: {
    setToken: (t: string | null) => void;
    setIsAuthenticated: (v: boolean) => void;
    setUser: (u: User | null) => void;
    setTenantId: (v: number | null) => void;
    setTenantName: (v: string | null) => void;
    setTenantCode: (v: string | null) => void;
    setTwoFactorEnabled: (v: boolean | null) => void;
  }
) => {
  setters.setToken(null);
  setters.setIsAuthenticated(hasBrowserSession());
  delete api.defaults.headers.common['Authorization'];
  if (snap.username) {
    setters.setUser({
      username: snap.username,
      displayName: snap.displayName || undefined,
      roles: snap.roles,
    } as User);
  } else {
    setters.setUser(null);
  }
  setters.setTenantId(snap.tenantId);
  setters.setTenantName(snap.tenantName);
  setters.setTenantCode(snap.tenantCode);
  setters.setTwoFactorEnabled(snap.twoFactorEnabled);
};

/** Aplica tenants solo si la API los mandó. refresh/switch no los envían: no vaciar la solapa. */
const applyTenantsFromAuthResponse = (
  data: LoginResponse,
  setTenantsState: (t: TenantInfo[]) => void,
  options?: { clearIfMissing?: boolean }
) => {
  if (data.tenants?.length) {
    setTenantsList(data.tenants);
    setTenantsState(data.tenants);
    return;
  }
  if (options?.clearIfMissing) {
    setTenantsList([]);
    setTenantsState([]);
  }
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const bootSnap = seedSessionFromLastUsedIfNeeded();
  const [token, setToken] = useState<string | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(() => hasBrowserSession());
  const [user, setUser] = useState<User | null>(() =>
    bootSnap.username
      ? ({
          username: bootSnap.username,
          displayName: bootSnap.displayName || undefined,
          roles: bootSnap.roles,
        } as User)
      : null
  );
  const [tenantId, setTenantId] = useState<number | null>(() => bootSnap.tenantId);
  const [tenantName, setTenantName] = useState<string | null>(() => bootSnap.tenantName);
  const [tenantCode, setTenantCode] = useState<string | null>(() => bootSnap.tenantCode);
  const [twoFactorEnabled, setTwoFactorEnabled] = useState<boolean | null>(
    () => bootSnap.twoFactorEnabled
  );
  const [tenants, setTenants] = useState<TenantInfo[]>(() => getTenantsList());
  const [authReady, setAuthReady] = useState(false);
  const resumeBlocked = useRef(isTabExplicitLogout());
  const bootSettled = useRef(false);

  const stateSetters = {
    setToken,
    setIsAuthenticated,
    setUser,
    setTenantId,
    setTenantName,
    setTenantCode,
    setTwoFactorEnabled,
  };

  const establishSession = (data: LoginResponse, twoFactorStatus: boolean) => {
    clearTabExplicitLogout();
    if (!data.username) {
      throw new Error('La API no devolvió el usuario de la sesión.');
    }
    if (!data.tabSessionId) {
      throw new Error('La API no devolvió la sesión de la solapa.');
    }

    persistTabSession({
      csrfToken: data.csrfToken,
      tabSessionId: data.tabSessionId,
      username: data.username,
      displayName: data.displayName,
      roles: data.roles || [],
      tenantId: data.tenantId ?? null,
      tenantName: data.tenantName ?? null,
      tenantCode: data.tenantCode ?? null,
      twoFactorEnabled: twoFactorStatus,
      updateLastUsed: true,
    });

    applyTenantsFromAuthResponse(data, setTenants, { clearIfMissing: true });

    applySessionToState(readTabSession(), stateSetters);
    sessionStorage.setItem(PWA_INSTALL_ELIGIBLE_KEY, '1');
    window.clarity?.('identify', data.username, undefined, undefined, data.displayName || data.username);
    checkForUpdates();
  };

  useEffect(() => {
    const snap = seedSessionFromLastUsedIfNeeded();
    applySessionToState(snap, stateSetters);
    setTenants(getTenantsList());
    if (hasBrowserSession()) {
      if (snap.username) {
        window.clarity?.('identify', snap.username, undefined, undefined, snap.displayName || snap.username);
      }
      checkForUpdates();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const channel = getAuthBroadcastChannel();
    if (!channel) return;

    const onMessage = (event: MessageEvent<AuthBroadcastMessage>) => {
      const msg = event.data;
      if (!msg || typeof msg !== 'object') return;

      if (msg.type === 'logout-all') {
        resumeBlocked.current = true;
        markTabExplicitLogout();
        clearGlobalSucursalCompaniaPrefs();
        clearTabSession();
        applySessionToState(readTabSession(), stateSetters);
        setTenants([]);
        if (window.location.pathname !== '/login') {
          window.location.replace('/login');
        }
        return;
      }

      if (msg.type === 'slot-probe' && bootSettled.current && hasBrowserSession()) {
        const current = getTabSessionId();
        if (current && current === msg.tabSessionId) {
          broadcastAuthMessage({ type: 'slot-owned', tabSessionId: current });
        }
      }
    };

    channel.addEventListener('message', onMessage);
    return () => channel.removeEventListener('message', onMessage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = async (username: string, password: string): Promise<TwoFactorChallengeResponse | null> => {
    try {
      clearTesoreriaFiltrosStorage();
      clearGlobalSucursalCompaniaPrefs();
      const data: LoginAttemptResponse = await authService.login(username, password);
      if (data.requiresTwoFactor === true) {
        return { requiresTwoFactor: true, challengeToken: 'cookie' };
      }

      establishSession(data, false);
      return null;
    } catch (error) {
      devError('Error al iniciar sesión:', error);
      throw error;
    }
  };

  const completeTwoFactorLogin = async (challengeToken: string, code: string) => {
    try {
      const data = await authService.loginWithTwoFactor(challengeToken, code);
      establishSession(data, true);
    } catch (error) {
      devError('Error al completar la autenticación de dos factores:', error);
      throw error;
    }
  };

  const logout = (options?: { allTabs?: boolean }) => {
    resumeBlocked.current = true;
    markTabExplicitLogout();
    const url = options?.allTabs ? '/api/auth/logout?all=true' : '/api/auth/logout';
    const headers = browserSessionHeaders('post');
    const pendingLogout = api.post(url, {}, { headers }).catch(() => undefined);
    sessionStorage.removeItem(PWA_INSTALL_DISMISSED_KEY);
    sessionStorage.removeItem(PWA_INSTALL_ELIGIBLE_KEY);
    clearTesoreriaFiltrosStorage();
    clearGlobalSucursalCompaniaPrefs();
    clearFechaOperativaSession();
    clearPermisosCache();
    clearTenantAdminGate();
    clearGrupoCajaStorage();

    clearTabSession();

    if (options?.allTabs) {
      clearAllAuthStorage();
      broadcastAuthMessage({ type: 'logout-all' });
    }

    delete api.defaults.headers.common['Authorization'];
    setUser(null);
    setToken(null);
    setTenantId(null);
    setTenantName(null);
    setTenantCode(null);
    setTwoFactorEnabled(null);
    setTenants([]);
    setIsAuthenticated(false);

    void pendingLogout.finally(() => {
      if (window.location.pathname !== '/login') {
        window.location.replace('/login');
      }
    });
  };

  const updateTwoFactorStatus = (enabled: boolean) => {
    setTwoFactorEnabled(enabled);
    if (hasBrowserSession()) {
      persistTabSession({
        twoFactorEnabled: enabled,
        updateLastUsed: false,
      });
    }
  };

  const refreshToken = async () => {
    try {
      if (!hasBrowserSession()) {
        logout();
        return;
      }

      const response = await api.post<LoginResponse>('/api/auth/refresh', {});
      const data = response.data;
      const snap = readTabSession();
      const roles = data.roles ?? snap.roles;
      const resolvedUsername = data.username || snap.username || '';
      const resolvedDisplayName = data.displayName || snap.displayName || undefined;
      const nextTenantId = data.tenantId ?? snap.tenantId;
      const nextTenantName = data.tenantName ?? snap.tenantName;
      const nextTenantCode = data.tenantCode ?? snap.tenantCode;

      persistTabSession({
        csrfToken: data.csrfToken,
        tabSessionId: data.tabSessionId,
        roles,
        username: resolvedUsername || null,
        displayName: resolvedDisplayName || null,
        tenantId: nextTenantId,
        tenantName: nextTenantName,
        tenantCode: nextTenantCode,
        updateLastUsed: true,
      });

      applyTenantsFromAuthResponse(data, setTenants);
      applySessionToState(readTabSession(), stateSetters);
    } catch (error) {
      devError('Error al actualizar el token:', error);
      logout();
    }
  };

  const switchTenant = async (newTenantId: number, options?: { reload?: boolean }) => {
    try {
      const response = await api.post<LoginResponse>('/api/auth/switchtenant', {
        tenantId: newTenantId,
      });
      const data = response.data;
      const roles = data.roles ?? readTabSession().roles;
      const username = data.username || readTabSession().username;
      const displayName = data.displayName || readTabSession().displayName;

      clearGlobalSucursalCompaniaPrefs();

      persistTabSession({
        csrfToken: data.csrfToken,
        tabSessionId: data.tabSessionId,
        roles,
        username: username || null,
        displayName: displayName || null,
        tenantId: data.tenantId ?? newTenantId,
        tenantName: data.tenantName ?? null,
        tenantCode: data.tenantCode ?? null,
        updateLastUsed: true,
      });

      applyTenantsFromAuthResponse(data, setTenants);
      applySessionToState(readTabSession(), stateSetters);
      writeLastUsedFromSession();

      if (options?.reload !== false) {
        window.location.reload();
      }
    } catch (error) {
      devError('Error al cambiar tenant:', error);
      throw error;
    }
  };

  useEffect(() => {
    const controller = new AbortController();
    let cancelled = false;

    const resumeBrowserSession = async () => {
      const tenantId = getLastUsedTenantId();
      const response = await api.post<LoginResponse>(
        '/api/auth/tabs/resume',
        tenantId != null ? { tenantId } : {}
      );
      if (cancelled || resumeBlocked.current || isTabExplicitLogout()) return;
      establishSession(response.data, false);
    };

    (async () => {
      try {
        if (resumeBlocked.current || isTabExplicitLogout()) return;

        const code = consumeTabHandoffQuery();
        if (code) {
          try {
            const response = await api.post<LoginResponse>('/api/auth/tabs/redeem', { code });
            if (cancelled || resumeBlocked.current || isTabExplicitLogout()) return;
            establishSession(response.data, false);
          } catch (error) {
            devError('Error al abrir la empresa en esta solapa:', error);
            if (!cancelled && window.location.pathname !== '/login') {
              window.location.replace('/login');
            }
          }
          return;
        }

        if (hasBrowserSession()) {
          const tabSessionId = getTabSessionId();
          if (!tabSessionId) return;
          const taken = await anotherTabOwnsSlot(tabSessionId, controller.signal);
          if (cancelled || resumeBlocked.current || isTabExplicitLogout()) return;
          if (!taken) return;
          try {
            await resumeBrowserSession();
          } catch (error) {
            devError('No se pudo separar la ranura de la pestaña duplicada:', error);
          }
          return;
        }

        await resumeBrowserSession();
      } catch (error) {
        devError('No se pudo reanudar la sesión en esta solapa:', error);
      } finally {
        if (!cancelled) {
          bootSettled.current = true;
          setAuthReady(true);
        }
      }
    })();

    return () => {
      cancelled = true;
      controller.abort();
    };
    // Una sola vez al abrir la solapa.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <AuthContext.Provider
      value={{
        authReady,
        isAuthenticated,
        user,
        token,
        tenantId,
        tenantName,
        tenantCode,
        tenants,
        twoFactorEnabled,
        updateTwoFactorStatus,
        login,
        completeTwoFactorLogin,
        logout,
        refreshToken,
        switchTenant,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth debe ser usado dentro de un AuthProvider');
  }
  return context;
};
